
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';
import { parseDiets, sortItemsByName } from '@/utils/format';
import OrderMenuCard from './OrderMenuCard.vue';
import AlphabeticalSortButton from '@/components/shared/AlphabeticalSortButton.vue';
import StaffOrderingFilter from '@/components/shared/StaffOrderingFilter.vue';

@Component<OrderMenu>({
	components: {
		OrderMenuCard,
		AlphabeticalSortButton,
		StaffOrderingFilter
	}
})
export default class OrderMenu extends Vue {
	@Prop({ type: Object, required: true }) private menu!: Menu;
	@Prop({ type: Array, required: true }) private allMenus!: Menu[];
	@Prop({ type: Array, required: true }) private featuredItems!: MenuItem[];
	@Prop({ type: Number, required: true, default: 0 }) private sectionsHeaderHeight!: number;
	@Prop({ type: Boolean, default: false }) private kioskMode!: boolean;
	@Getter('getModifyingOrder', { namespace: 'orders' }) isModifyingOrder!: boolean;
	@Getter('getItemSort', { namespace: 'orders' }) itemSort!: OrderItemSort;
	@Getter('getItemDiets', { namespace: 'orders' }) selectedDiets!: string[];
	@Action('setItemSort', { namespace: 'orders' }) private setItemSort!: (sort: OrderItemSort) => void;
	@Action('setItemDiets', { namespace: 'orders' }) private setItemDiets!: (diets: string[]) => void;
	@Getter('getRestaurant', { namespace: 'auth' }) restaurant!: Restaurant;

	public selectedSection: number = 0;
	private windowHeight: number = 0;

	/**
	 * Filters out sections with no items
	 *
	 * @return {MenuSection[]}
	 */
	private get filteredSections(): MenuSection[] {
		const sections: any[] = [];

		// Add featured items as first section if we have any
		if (this.featuredItems.length) {
			const featured = this.applyDietFilter(this.featuredItems);
			if (featured.length) {
				sections.push({
					id: 'featured',
					name: this.$t('menu.featured_items'),
					slug: 'featured',
					items: featured
				});
			}
		}

		// Add regular sections (only those with items after diet filtering)
		if (this.menu?.sections?.length) {
			const menuSections = this.menu!.sections!
				.map((section: MenuSection) => ({
					...section,
					items: this.applyDietFilter(section.items || [])
				}))
				.filter((section: MenuSection) => section.items && section.items.length > 0);
			sections.push(...menuSections);
		}

		return sections;
	}

	/**
	 * `filteredSections` with each section's items sorted by the user's current
	 * sort selection. Section order itself is preserved.
	 * otherwise scroll-driven re-renders via `selectedSection` would re-sort
	 * every item on every tick.
	 *
	 * @return {MenuSection[]}
	 */
	private get sectionsWithSortedItems(): MenuSection[] {
		return this.filteredSections.map((section: MenuSection) => ({
			...section,
			items: this.applyItemSort(section.items || [])
		}));
	}

	/**
	 * Featured items with the user's current sort applied (v-else branch).
	 *
	 * @return {MenuItem[]}
	 */
	private get sortedFeaturedItems(): MenuItem[] {
		return this.applyItemSort(this.applyDietFilter(this.featuredItems));
	}

	/**
	 * Unique list of diet values that are assigned to at least one item
	 * across every menu (and the featured items). Used to scope the diet
	 * filter to only the diets actually present in the order context, so
	 * the chip list and active filter stay consistent when switching menus.
	 *
	 * @return {string[]}
	 */
	private get availableDiets(): string[] {
		const items: MenuItem[] = [
			...this.featuredItems,
			...this.allMenus.flatMap((menu: Menu) => [
				...(menu.sections || []).flatMap((section: MenuSection) => section.items || []),
				...(menu.items || [])
			])
		];
		return [...new Set(items.flatMap((item: MenuItem) => parseDiets(item)))];
	}

	/**
	 * Regular menu items with the user's current sort applied (v-else branch).
	 *
	 * @return {MenuItem[]}
	 */
	private get sortedMenuItems(): MenuItem[] {
		return this.applyItemSort(this.applyDietFilter(this.menu.items || []));
	}

	/**
	 * Checks if any item in the menu has an image
	 * * Staff ordering only, not applicable when modifying a generic catering order
	 * * This is due to staff ordering requiring a more condensed view of the menu
	 * * Bowlero does not need images
	 * TODO: Remove the restaurant.group check when all Bowlero locations are ready to use restaurant.groups
	 *
	 * @return {boolean}
	 */
	private get doesMenuHaveImages(): boolean {
		if (this.restaurant.group === 'bowlero' || this.restaurant.groups?.includes('bowlero')) {
			return false;
		}
		if (this.isModifyingOrder) {
			return true;
		}
		if (this.filteredSections.length) {
			return this.filteredSections.some((section: MenuSection) => section.items?.some((item: MenuItem) => item.image));
		}
		return this.menu.items?.some((item: MenuItem) => item.image) || false;
	}

	private mounted(): void {
		this.windowHeight = window.innerHeight;
		// Ensure first section is selected on mount
		if (this.filteredSections.length) {
			this.selectedSection = 0;
		}
	}

	/**
	 * Returns `items` sorted by the current `itemSort` state, or the original
	 * array untouched when no sort is active.
	 *
	 * @param {MenuItem[]} items
	 * @return {MenuItem[]}
	 */
	private applyItemSort(items: MenuItem[]): MenuItem[] {
		if (!this.itemSort) {
			return items;
		}
		return sortItemsByName(items, this.itemSort);
	}

	/**
	 * Returns `items` filtered to those matching all currently-selected
	 * diets, or the original array when no diet filter is active.
	 *
	 * @param {MenuItem[]} items
	 * @return {MenuItem[]}
	 */
	private applyDietFilter(items: MenuItem[]): MenuItem[] {
		if (!this.selectedDiets.length) {
			return items;
		}
		return items.filter((item: MenuItem) => {
			const itemDiets = parseDiets(item);
			return this.selectedDiets.every((diet: string) => itemDiets.includes(diet));
		});
	}

	/**
	 * Scrolls to the selected section while taking into account the height of the sticky headers
	 * Temporarily remove the scroll listener while scrolling to have an accurate section highlighting
	 *
	 * @param {string} sectionId - The id of the section to scroll to
	 * @return {void}
	 */
	private scrollToSection(sectionId: string): void {
		this.$emit('toggle-scroll-listener', false);

		const section = document.getElementById(`section-${sectionId}`);
		const menusHeader = document.getElementById('order-menus-selector');

		if (section && this.sectionsHeaderHeight && menusHeader) {
			let menusHeaderHeight = menusHeader.offsetHeight;
			const targetOffset = window.scrollY + section.getBoundingClientRect().top - this.sectionsHeaderHeight - menusHeaderHeight;
			this.$vuetify.goTo(targetOffset, { easing: 'easeInOutQuad', duration: 400 });

			// Needs to be longer than the scroll duration so that goTo doesn't trigger handleScroll
			setTimeout(() => this.$emit('toggle-scroll-listener', true), 500);
		}
	}

	/**
	 * Listen to the scroll event and set the selected menu or section based on Y axis
	 * * Public method as it is used by OrderMenus component
	 *
	 * @return {void}
	 */
	public handleScroll(): void {
		if (this.filteredSections.length > 1) {
			const yPositions: number[] = this.getYPositionsOfElements(this.filteredSections);
			this.selectedSection = this.getSelectionFromVisibleSections(yPositions, this.windowHeight);
		}
	}

	/**
	 * Get the Y positions on the screen of the sections
	 *
	 * @param {MenuSection[]} elements
	 * @return {number[]}
	 */
	private getYPositionsOfElements(elements: MenuSection[]): number[] {
		const yPositions: number[] = [];

		elements.forEach((element) => {
			const docElement = document.getElementById(`section-${element.id}`);
			if (docElement) {
				// 112 is the height of the sticky menu and section headers (v-tabs)
				yPositions.push(docElement.getBoundingClientRect().top - 112);
			}
			else {
				yPositions.push(-1000);
			}
		});

		return yPositions;
	}

	/**
	 * Get the selected menu or section based on scroll position
	 *
	 * @param {number[]} yPositions
	 * @param {number} windowHeight
	 * @return {number} index of the selected element in view
	 */
	private getSelectionFromVisibleSections(yPositions: number[], windowHeight: number): number {
		const pageHalfwayPoint = windowHeight / 2;

		if (yPositions[0] > pageHalfwayPoint) {
			return 0;
		}

		for (let i = 0; i < yPositions.length; i++) {
			if (i + 1 === yPositions.length || (yPositions[i] < pageHalfwayPoint && yPositions[i + 1] > pageHalfwayPoint)) {
				return i;
			}
		}
		return 0;
	}
}
